import { CheckCircle, LockSimple, Moon, Sun } from "@phosphor-icons/react";
import type React from "react";
import { lazy, useCallback, useMemo, useState } from "react";
import { match } from "ts-pattern";

import { DialogBody } from "~/components/DialogBody";
import { DialogFooter } from "~/components/DialogFooter";
import { DialogHeader } from "~/components/DialogHeader";
import { DialogTitle } from "~/components/DialogTitle";
import { Modal } from "~/components/Modal";
import { useLocale } from "~/hooks/locale";
import { useTheme } from "~/hooks/theme";
import type { Theme } from "~/theme";
import { trackEvent } from "~/utils/analytics";

import { LineItem } from "./LineItem";
import { UnlockTheme } from "./UnlockTheme";

const AuthForm = lazy(() => import("~/components/AuthForm"));

export type ThemeDetails = {
  name: string;
  description?: string;
  price: number;
};

export const ThemeOption: React.FC<{
  themeKey: Theme;
  details: ThemeDetails;
  balance: number;
  unlockedThemes?: string[];
}> = ({ themeKey, details, balance, unlockedThemes }) => {
  const [theme, setTheme] = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const remainder = useMemo(
    () => balance - details.price,
    [balance, details.price],
  );

  const { t } = useLocale();

  const isLocked = useMemo(
    () => details.price > 0 && !unlockedThemes?.includes(themeKey),
    [details.price, themeKey, unlockedThemes],
  );

  return (
    <>
      <button
        data-theme={themeKey}
        onClick={() => {
          if (isLocked) {
            setIsModalOpen(true);
            return;
          }

          setTheme(themeKey);
          trackEvent("change_theme");
        }}
        className="relative flex aspect-tv flex-col items-center justify-center gap-2 rounded border bg-[image:var(--bg-image)] bg-cover bg-center bg-no-repeat"
      >
        {theme === themeKey && (
          <CheckCircle
            className="absolute right-1 top-1 size-6 text-accent"
            weight="fill"
          />
        )}
        {match(themeKey)
          .with("knoword", () => <Sun className="size-8" />)
          .with("dark", () => <Moon className="size-8" />)
          .otherwise(
            () =>
              isLocked && (
                <div className="rounded-full bg-base-100 p-2">
                  <LockSimple className="size-5" weight="bold" />
                </div>
              ),
          )}
        {}
      </button>
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {Array.isArray(unlockedThemes) ? (
          // if unlockedThemes is an array, it means the user is logged in
          <>
            <DialogHeader>{t("unlock_theme")}</DialogHeader>
            <DialogBody>
              <div className="grid gap-4 sm:grid-cols-2 md:gap-6">
                <div
                  data-theme={themeKey}
                  className="flex aspect-tv rounded border bg-[image:var(--bg-image)] bg-cover bg-center bg-no-repeat"
                >
                  <div className="flex grow items-center justify-center bg-gradient-to-t from-base-100">
                    <div className="space-y-0.5 text-center">
                      <DialogTitle>{details.name}</DialogTitle>
                      {details.description && <p>{details.description}</p>}
                    </div>
                  </div>
                </div>
                <div className="space-y-5">
                  <div className="space-y-3">
                    <LineItem label={t("current_balance")} value={balance} />
                    <LineItem label={t("theme")} value={details.price} />
                    <div className="h-px bg-base-300" />
                    <LineItem label={t("new_balance")} value={remainder} />
                  </div>
                  <p className="text-sm">{t("insufficient_balance")}</p>
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <div className="grid grow grid-cols-2 gap-3">
                <button className="btn" onClick={closeModal}>
                  {t("go_back")}
                </button>
                <UnlockTheme
                  isDisabled={remainder < 0}
                  themeKey={themeKey}
                  amount={details.price}
                />
              </div>
            </DialogFooter>
          </>
        ) : (
          <AuthForm />
        )}
      </Modal>
    </>
  );
};
